<template>
  <v-dialog persistent fullscreen :origin="dialogOrigin" :value="active" :close-on-back="closeOnBack" content-class="auth-dialog">
    <v-card dark flat class="auth-shell auth-shell--login">
      <div class="auth-login-layout">
        <v-btn icon class="auth-close-button" aria-label="Login schliessen" @click="$emit('close')">
          <v-icon aria-hidden="true">mdi-close</v-icon>
        </v-btn>

        <aside class="auth-story" aria-label="WELL COME FIT App">
          <img class="auth-brand-logo" :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" />

          <div class="auth-story__copy">
            <span class="auth-eyebrow">WELL COME FIT App</span>
            <h1>Dein Training.<br /><span>Dein Moment.</span></h1>
            <p>Dein Studio, deine Kurse und dein Fortschritt – immer griffbereit, wenn du loslegen willst.</p>
          </div>

          <div class="auth-story__foot">
            <v-icon aria-hidden="true">mdi-shield-lock-outline</v-icon>
            <span>Sicher anmelden und direkt durchstarten.</span>
          </div>
        </aside>

        <main class="auth-login-panel">
          <div class="auth-mobile-brand" aria-label="WELL COME FIT App">
            <img class="auth-brand-logo" :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" />
            <span class="auth-app-label">App</span>
          </div>

          <header class="auth-panel-header">
            <span class="auth-eyebrow">{{ authEyebrow }}</span>
            <h2>{{ authTitle }}</h2>
            <p>{{ authDescription }}</p>
          </header>

          <v-form ref="loginForm" class="auth-form" lazy-validation novalidate @submit.prevent="login">
            <div v-if="forgottenLink" class="auth-form-row">
              <v-text-field
                v-model="newPassword.first"
                class="auth-field"
                outlined
                color="primary"
                label="Neues Passwort"
                name="new-password"
                autocomplete="new-password"
                prepend-inner-icon="mdi-lock-outline"
                :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                :type="showPassword ? 'text' : 'password'"
                :rules="rules.newPassword"
                hide-details="auto"
                @click:append="showPassword = !showPassword"></v-text-field>
              <v-text-field
                v-model="newPassword.second"
                class="auth-field"
                outlined
                color="primary"
                label="Neues Passwort wiederholen"
                name="new-password-confirmation"
                autocomplete="new-password"
                prepend-inner-icon="mdi-lock-check-outline"
                :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                :type="showPassword ? 'text' : 'password'"
                :rules="rules.newPassword"
                hide-details="auto"
                @click:append="showPassword = !showPassword"></v-text-field>
            </div>

            <template v-else>
              <div class="auth-form-row">
                <v-text-field
                  v-model.trim="formData.email"
                  class="auth-field"
                  outlined
                  color="primary"
                  type="email"
                  inputmode="email"
                  name="email"
                  autocomplete="email"
                  label="E-Mail-Adresse"
                  placeholder="name@beispiel.ch"
                  prepend-inner-icon="mdi-email-outline"
                  :rules="rules.email"
                  hide-details="auto"
                  required
                  autofocus></v-text-field>
                <v-text-field
                  v-if="!forgotPassword"
                  v-model="formData.password"
                  class="auth-field"
                  outlined
                  color="primary"
                  label="Passwort"
                  name="password"
                  autocomplete="current-password"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  :type="showPassword ? 'text' : 'password'"
                  :rules="rules.password"
                  hide-details="auto"
                  required
                  @click:append="showPassword = !showPassword"></v-text-field>
              </div>

              <div v-if="!forgotPassword" class="auth-forgot-row">
                <button type="button" class="auth-text-button" @click="openForgotPassword">Passwort vergessen?</button>
              </div>
            </template>

            <v-expand-transition>
              <v-alert v-if="statusMessage" :type="statusType" text dense class="auth-inline-alert" role="status">
                {{ statusMessage }}
              </v-alert>
            </v-expand-transition>

            <div class="auth-form-actions" :class="{ 'auth-form-actions--split': forgotPassword }">
              <v-btn v-if="forgotPassword" depressed class="auth-secondary-button" :disabled="isSubmitting" @click="showLoginMode">
                <v-icon left aria-hidden="true">mdi-arrow-left</v-icon>
                Zurück
              </v-btn>
              <v-btn type="submit" depressed block class="auth-primary-button" :loading="isSubmitting" :disabled="!dataEntered || isSubmitting">
                {{ submitLabel }}
                <v-icon right aria-hidden="true">{{ submitIcon }}</v-icon>
              </v-btn>
            </div>

            <p v-if="showModeSwitch && !forgottenLink && !forgotPassword" class="auth-switch-copy">
              Noch kein Account?
              <button type="button" class="auth-text-button" @click="$emit('show-register')">Jetzt registrieren</button>
            </p>
          </v-form>
        </main>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import "./authTheme.scss"
import { brandLogo } from "@/constants/brandAssets"
import api from "../../api"
import { EventBus } from "../../event-bus"

export default {
  name: "AuthLogin",
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    closeOnBack: {
      type: Boolean,
      default: true,
    },
    autoLogin: {
      type: Boolean,
      default: true,
    },
    dialogOrigin: {
      type: String,
      default: "50% 50%",
    },
    showModeSwitch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      brandLogo,
      showPassword: false,
      forgotPassword: false,
      isSubmitting: false,
      statusMessage: "",
      statusType: "error",
      newPassword: {
        first: "",
        second: "",
      },
      formData: {
        email: "",
        password: "",
      },
      rules: {
        email: [(v) => !!v || "Die E-Mail wird benötigt", (v) => /^[^@]+@[^@]+\.[^@]+$/.test(v) || "Das ist keine gültige E-Mail"],
        password: [(v) => !!v || "Bitte gib dein Passwort ein."],
        newPassword: [(v) => !!v || "Bitte gib dein neues Passwort ein.", (v) => (v && v.length >= 8) || "Mindestens 8 Zeichen eingeben."],
      },
    }
  },
  computed: {
    forgottenLink() {
      return this.$route.name === "Forgot"
    },
    dataEntered() {
      if (this.forgottenLink) return Boolean(this.newPassword.first && this.newPassword.second)

      const emailIsValid = /^[^@]+@[^@]+\.[^@]+$/.test(this.formData.email || "")
      return emailIsValid && (this.forgotPassword || Boolean(this.formData.password))
    },
    authEyebrow() {
      if (this.forgottenLink) return "Sicherer Zugang"
      if (this.forgotPassword) return "Zugang wiederherstellen"
      return "Willkommen zurück"
    },
    authTitle() {
      if (this.forgottenLink) return "Neues Passwort wählen"
      if (this.forgotPassword) return "Passwort vergessen?"
      return "Bereit für dein Training?"
    },
    authDescription() {
      if (this.forgottenLink) return "Lege jetzt ein neues, sicheres Passwort für deinen Account fest."
      if (this.forgotPassword) return "Gib deine E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen."
      return "Melde dich mit deinem WELL COME FIT Account an und mach dort weiter, wo du aufgehört hast."
    },
    submitLabel() {
      if (this.forgottenLink) return "Passwort speichern"
      if (this.forgotPassword) return "Link anfordern"
      return "Anmelden"
    },
    submitIcon() {
      if (this.forgottenLink) return "mdi-check"
      if (this.forgotPassword) return "mdi-email-fast-outline"
      return "mdi-arrow-right"
    },
  },
  watch: {
    active(value) {
      if (value) this.clearStatus()
    },
  },
  methods: {
    clearStatus() {
      this.statusMessage = ""
      this.statusType = "error"
    },
    setStatus(message, type = "error") {
      this.statusMessage = message
      this.statusType = type
    },
    openForgotPassword() {
      this.clearStatus()
      this.forgotPassword = true
      this.$nextTick(() => this.$refs.loginForm && this.$refs.loginForm.resetValidation())
    },
    showLoginMode() {
      this.clearStatus()
      this.forgotPassword = false
      this.$nextTick(() => this.$refs.loginForm && this.$refs.loginForm.resetValidation())
    },
    async login() {
      if (this.isSubmitting || !this.$refs.loginForm.validate()) return

      this.clearStatus()

      if (this.forgottenLink && this.newPassword.first !== this.newPassword.second) {
        this.setStatus("Die beiden Passwörter stimmen nicht überein.")
        return
      }

      this.isSubmitting = true

      try {
        if (this.forgottenLink) {
          const data = await api.changePassword({
            key: location.search.substring(1),
            password: this.newPassword.first,
          })

          if (!data.status) {
            this.setStatus("Das Passwort konnte nicht gespeichert werden. Bitte versuche es erneut.")
            return
          }

          this.newPassword.first = ""
          this.newPassword.second = ""
          this.setStatus("Dein Passwort wurde aktualisiert. Du kannst dich jetzt anmelden.", "success")
          await this.$router.push("/login")
          return
        }

        if (this.forgotPassword) {
          await api.resetPassword({ email: this.formData.email })
          this.setStatus(
            "Wenn die E-Mail-Adresse registriert ist, haben wir dir einen Link gesendet. Bitte prüfe auch deinen Spam-Ordner.",
            "success",
          )
          return
        }

        const data = await api.authenticate({
          email: this.formData.email,
          password: this.formData.password,
        })

        if (this.autoLogin) {
          EventBus.$emit("login", data)
        } else {
          this.$emit("login", data)
        }
      } catch (error) {
        if (this.forgottenLink) {
          this.setStatus("Das Passwort konnte nicht gespeichert werden. Bitte fordere bei Bedarf einen neuen Link an.")
        } else if (this.forgotPassword) {
          this.setStatus("Die Anfrage konnte gerade nicht gesendet werden. Bitte versuche es erneut.")
        } else {
          this.setStatus("E-Mail-Adresse oder Passwort sind nicht korrekt.")
        }
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>
